import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { RefreshCw, Printer, ChevronRight, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIE_COLORS = [
  "#E8762D", "#1F6B3A", "#2D6CB8", "#A05195", "#D45087",
  "#F95D6A", "#FF7C43", "#FFA600", "#665191", "#003F5C",
];

const fmtMin = (m) => {
  if (m === null || m === undefined) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h${String(r).padStart(2, "0")}`;
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoOffset = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const StudyDashboard = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filtros livres (sem min/max)
  const [range, setRange] = useState({ from: "", to: "" });
  const [search, setSearch] = useState("");

  // Comparação de período
  const [compareOn, setCompareOn] = useState(false);
  const [compareRange, setCompareRange] = useState({ from: "", to: "" });
  const [compareData, setCompareData] = useState(null);

  // Carrega clientes acessíveis
  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await axios.get(`${API}/parameters/accessible`);
        const list = Array.isArray(res.data) ? res.data : [];
        setClients(list);
        if (list.length > 0) setSelectedClientId(list[0].id);
        else setError("Nenhum cliente disponível para o seu usuário.");
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 403) {
          setError(detail || "Seu administrador ainda não liberou o acesso ao Dashboard.");
        } else {
          setError(detail || "Erro ao carregar clientes.");
        }
      }
    };
    loadClients();
  }, []);

  // Carrega dados do dashboard (e do período de comparação, se ativo)
  const loadData = useCallback(async () => {
    if (!selectedClientId) return;
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.start_date = range.from;
      if (range.to) params.end_date = range.to;
      const res = await axios.get(`${API}/analytics/study/${selectedClientId}`, { params });
      setData(res.data);

      if (compareOn && compareRange.from && compareRange.to) {
        const res2 = await axios.get(`${API}/analytics/study/${selectedClientId}`, {
          params: { start_date: compareRange.from, end_date: compareRange.to },
        });
        setCompareData(res2.data);
      } else {
        setCompareData(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar dashboard");
      setData(null);
      setCompareData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, range, compareOn, compareRange]);

  useEffect(() => { loadData(); }, [loadData]);

  const [downloading, setDownloading] = useState(false);
  const downloadXlsx = async () => {
    if (!selectedClientId) return;
    setDownloading(true);
    try {
      const params = {};
      if (range.from) params.start_date = range.from;
      if (range.to) params.end_date = range.to;
      const res = await axios.get(
        `${API}/analytics/study/${selectedClientId}/export.xlsx`,
        { params, responseType: "blob" }
      );
      // Extrai nome de arquivo do header Content-Disposition (se presente)
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `RDO_${selectedClient?.client_name || "export"}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel baixado com sucesso!");
    } catch (e) {
      toast.error("Erro ao gerar Excel");
    } finally {
      setDownloading(false);
    }
  };

  const setPreviousEquivalentPeriod = () => {
    if (!range.from || !range.to) {
      toast.info("Defina o período principal antes de calcular o anterior.");
      return;
    }
    const f = new Date(range.from + "T00:00:00");
    const t = new Date(range.to + "T00:00:00");
    const days = Math.round((t - f) / 86400000) + 1;
    const newTo = new Date(f); newTo.setDate(newTo.getDate() - 1);
    const newFrom = new Date(newTo); newFrom.setDate(newFrom.getDate() - days + 1);
    setCompareRange({
      from: newFrom.toISOString().slice(0, 10),
      to: newTo.toISOString().slice(0, 10),
    });
  };

  // Computa stats e agregados
  const stats = useMemo(() => computeStats(data), [data]);
  const compareStats = useMemo(() => computeStats(compareData), [compareData]);

  const selectedClient = clients.find(c => c.id === selectedClientId);

  // Render error state
  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center" data-testid="dashboard-error">
        <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <h3 className="text-lg font-semibold mb-2">Acesso ao Dashboard não disponível</h3>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="study-dashboard">
      {/* Print-only styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break-avoid { break-inside: avoid; }
        }
      `}</style>

      {/* HEADER + Seletor + Filtros */}
      <Card className="no-print">
        <CardContent className="pt-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500 uppercase tracking-wide mb-1">Cliente</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger data-testid="client-selector"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id} data-testid={`client-opt-${c.id}`}>
                      {c.client_name || c.name || c.id}
                      {c.status === "FINALIZADO" ? " (Finalizado)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wide mb-1">De</Label>
              <Input
                type="date"
                data-testid="date-from"
                value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
                className="font-mono"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wide mb-1">Até</Label>
              <Input
                type="date"
                data-testid="date-to"
                value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
                className="font-mono"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <Button size="sm" variant="outline" data-testid="preset-all" onClick={() => setRange({ from: "", to: "" })}>↺ Tudo</Button>
              <Button size="sm" variant="outline" data-testid="preset-7d" onClick={() => setRange({ from: isoOffset(6), to: isoToday() })}>7d</Button>
              <Button size="sm" variant="outline" data-testid="preset-30d" onClick={() => setRange({ from: isoOffset(29), to: isoToday() })}>30d</Button>
              <Button size="sm" variant="outline" data-testid="preset-90d" onClick={() => setRange({ from: isoOffset(89), to: isoToday() })}>90d</Button>
              <Button size="sm" variant="outline" data-testid="preset-365d" onClick={() => setRange({ from: isoOffset(364), to: isoToday() })}>1 ano</Button>
            </div>
            <Button onClick={loadData} disabled={loading || !selectedClientId} data-testid="refresh-btn" className="bg-[#1F6B3A] hover:bg-[#175129]">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Carregando..." : "Atualizar"}
            </Button>
            <div className="flex gap-2 flex-shrink-0" data-testid="export-actions">
              <Button variant="outline" onClick={() => window.print()} data-testid="print-btn">
                <Printer className="w-4 h-4 mr-2" /> Imprimir / PDF
              </Button>
              <Button
                variant="outline"
                onClick={downloadXlsx}
                disabled={downloading || !selectedClientId}
                data-testid="export-xlsx-btn"
                className="border-[#1F6B3A] text-[#1F6B3A] hover:bg-[#1F6B3A] hover:text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {downloading ? "Gerando..." : "Baixar Excel"}
              </Button>
            </div>
          </div>

          {/* Toggle de comparação */}
          <div className="mt-4 flex flex-wrap items-end gap-3 border-t pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={compareOn}
                onChange={(e) => setCompareOn(e.target.checked)}
                className="w-4 h-4"
                data-testid="compare-toggle"
              />
              <span className="font-medium text-slate-700">📊 Comparar com outro período</span>
            </label>
            {compareOn && (
              <>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide mb-1">Comparar De</Label>
                  <Input
                    type="date"
                    data-testid="compare-from"
                    value={compareRange.from}
                    onChange={(e) => setCompareRange({ ...compareRange, from: e.target.value })}
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase tracking-wide mb-1">Comparar Até</Label>
                  <Input
                    type="date"
                    data-testid="compare-to"
                    value={compareRange.to}
                    onChange={(e) => setCompareRange({ ...compareRange, to: e.target.value })}
                    className="font-mono"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={setPreviousEquivalentPeriod} data-testid="compare-prev-btn">
                  ⏪ Período anterior equivalente
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* HEADER de apresentação (visível em print também) */}
      <div className="bg-[#1F6B3A] text-white rounded-lg px-6 py-5 print-break-avoid">
        <div className="text-xs uppercase tracking-widest opacity-80">LocService — Dashboard Analítico</div>
        <h1 className="text-2xl md:text-3xl font-bold mt-1" data-testid="client-title">
          {selectedClient?.client_name || selectedClient?.name || "—"}
          {selectedClient?.contract_number && (
            <span className="text-base opacity-80 ml-2">· {selectedClient.contract_number}</span>
          )}
        </h1>
        <p className="text-sm opacity-90 mt-1">
          {range.from || range.to
            ? `Período: ${range.from || "início"} → ${range.to || "hoje"}`
            : "Período completo"} · {stats.totalReports} relatório{stats.totalReports === 1 ? "" : "s"}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-break-avoid">
        <KPI label="Relatórios" value={stats.totalReports} color="text-[#1F6B3A]" testid="kpi-reports" />
        <KPI label="Impedimentos" value={stats.totalImps} color="text-[#E8762D]" testid="kpi-imps" />
        <KPI label="Horas Perdidas" value={fmtMin(stats.totalMin)} sub="Soma das durações" testid="kpi-hours" />
        <KPI label="Média / Dia c/ Imp." value={fmtMin(stats.avgMin)} sub="≥ 1 impedimento" testid="kpi-avg" />
      </div>

      {/* COMPARAÇÃO */}
      {compareOn && compareData && (
        <Card className="print-break-avoid" data-testid="compare-section">
          <CardHeader className="pb-2"><CardTitle className="text-base">Comparação entre Períodos</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs text-slate-500 mb-3">
              <strong>Atual:</strong> {range.from || "início"} → {range.to || "hoje"} ·{" "}
              <strong>Comparado:</strong> {compareRange.from} → {compareRange.to}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <CompareCard label="Relatórios" cur={stats.totalReports} prev={compareStats.totalReports} />
              <CompareCard label="Impedimentos" cur={stats.totalImps} prev={compareStats.totalImps} />
              <CompareCard label="Horas Perdidas" cur={stats.totalMin} prev={compareStats.totalMin} formatter={fmtMin} />
              <CompareCard label="Média/Dia c/ Imp." cur={stats.avgMin} prev={compareStats.avgMin} formatter={fmtMin} />
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="text-center py-12 text-slate-500">🔄 Carregando dados...</div>
      )}

      {data && stats.totalReports === 0 && !loading && (
        <div className="text-center py-10 bg-amber-50 border border-amber-200 rounded-lg text-slate-700">
          Nenhum RDO encontrado para este cliente no período selecionado.
        </div>
      )}

      {data && stats.totalReports > 0 && (
        <>
          {/* Ranking por tipo (doughnut + tabela) */}
          <Card className="print-break-avoid">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Ranking de Impedimentos</CardTitle>
              <span className="text-xs text-slate-500">{range.from || "início"} → {range.to || "hoje"}</span>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={stats.byTypeEntries.map(([k, v]) => ({ name: k, value: +(v.mins / 60).toFixed(2) }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%" cy="50%"
                        innerRadius={50}
                        outerRadius={100}
                      >
                        {stats.byTypeEntries.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${v}h`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <table className="w-full text-sm" data-testid="pie-table">
                    <thead className="text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left py-1"></th>
                        <th className="text-left py-1">Tipo</th>
                        <th className="text-right py-1">Horas</th>
                        <th className="text-right py-1">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stats.byTypeEntries.length === 0 && (
                        <tr><td colSpan={4} className="py-3 text-center text-slate-400">Sem impedimentos</td></tr>
                      )}
                      {stats.byTypeEntries.map(([tipo, v], idx) => {
                        const pct = stats.totalMin > 0 ? ((v.mins / stats.totalMin) * 100).toFixed(1) : "0.0";
                        return (
                          <tr key={tipo}>
                            <td className="py-1.5"><span style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }} className="inline-block w-3 h-3 rounded" /></td>
                            <td className="py-1.5 pr-2">{tipo} <span className="text-xs text-slate-400">({v.count})</span></td>
                            <td className="py-1.5 text-right font-mono font-semibold">{fmtMin(v.mins)}</td>
                            <td className="py-1.5 text-right text-slate-500">{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barras horizontais + Linha diária */}
          <div className="grid lg:grid-cols-2 gap-4 print-break-avoid">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Impedimentos por Tipo</CardTitle></CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={stats.byTypeEntries.map(([k, v]) => ({ tipo: k, horas: +(v.mins / 60).toFixed(2), count: v.count }))} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" fontSize={11} />
                    <YAxis type="category" dataKey="tipo" fontSize={10} width={130} />
                    <Tooltip formatter={(v, _n, p) => [`${v}h · ${p.payload.count} ocorrência(s)`, "Horas"]} />
                    <Bar dataKey="horas" fill="#E8762D" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Horas Perdidas por Dia</CardTitle></CardHeader>
              <CardContent style={{ height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={stats.dailyHours}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(v) => `${v}h`} />
                    <Line type="monotone" dataKey="hours" stroke="#1F6B3A" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Heatmap */}
          <Card className="print-break-avoid">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Heatmap — Padrão de Impedimentos por Dia da Semana</CardTitle>
              <span className="text-xs text-slate-500">Quanto mais escuro, mais horas perdidas</span>
            </CardHeader>
            <CardContent>
              <Heatmap dayMins={stats.dayMinsMap} />
            </CardContent>
          </Card>

          {/* Tabela de impedimentos */}
          <Card className="print-break-avoid">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-base">Tabela de Impedimentos</CardTitle>
                <Input
                  type="text"
                  placeholder="🔍 Filtrar por tipo / comentário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-72 no-print"
                  data-testid="imp-search"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">Data</th>
                      <th className="text-left px-4 py-3">Tipo</th>
                      <th className="text-left px-4 py-3">Início</th>
                      <th className="text-right px-4 py-3">Duração</th>
                      <th className="text-left px-4 py-3">Comentário</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-testid="imp-tbody">
                    {filterImps(stats.allImps, search).length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Nenhum registro</td></tr>
                    ) : (
                      filterImps(stats.allImps, search).map((i, idx) => (
                        <tr key={`${i.data_relatorio}-${idx}`} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 font-mono text-xs">{i.data_relatorio}</td>
                          <td className="px-4 py-2.5">{i.tipo}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{i.horario_inicio || "—"}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmtMin(i.duracao_minutos)}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md">
                            {(i.comentario || "").substring(0, 200)}{(i.comentario || "").length > 200 ? "…" : ""}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Detalhe diário */}
          <div className="space-y-2" data-testid="daily-cards">
            <h3 className="font-semibold text-slate-700">Detalhe Diário — Atividades & Impedimentos</h3>
            {(data.relatorios || []).map(r => (
              <DailyCard key={r.id} report={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============== Subcomponentes ==============

const KPI = ({ label, value, sub, color = "text-slate-700", testid }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid={testid}>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    <div className={`text-3xl font-bold mt-1 ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const CompareCard = ({ label, cur, prev, formatter }) => {
  const fmt = formatter || ((v) => v);
  let deltaHtml;
  if (prev === 0 && cur === 0) deltaHtml = <span className="text-slate-400">—</span>;
  else if (prev === 0) deltaHtml = <span className="text-orange-600">↑ novo</span>;
  else {
    const pct = ((cur - prev) / prev) * 100;
    const arrow = cur > prev ? "↑" : cur < prev ? "↓" : "→";
    const color = cur > prev ? "text-red-600" : cur < prev ? "text-green-600" : "text-slate-500";
    deltaHtml = <span className={color}>{arrow} {Math.abs(pct).toFixed(1)}%</span>;
  }
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-2xl font-bold text-slate-700">{fmt(cur)}</div>
        <div className="text-xs">{deltaHtml}</div>
      </div>
      <div className="text-xs text-slate-400 mt-1">Anterior: {fmt(prev)}</div>
    </div>
  );
};

const DailyCard = ({ report }) => {
  const [open, setOpen] = useState(false);
  const horas = (report.impedimentos || []).reduce((s, x) => s + (x.duracao_minutos || 0), 0);
  const i = (report.impedimentos || []).length;
  const m = (report.atividades_manha || []).length;
  const t = (report.atividades_tarde || []).length;
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 text-left"
        data-testid={`daily-toggle-${report.data_relatorio}`}
      >
        <div className="flex items-center gap-3">
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="font-semibold text-slate-700 font-mono">{report.data_relatorio}</span>
          <span className="text-xs text-slate-500">manhã: {m} · tarde: {t} · impedimentos: {i}</span>
        </div>
        <Badge className={i > 0 ? "bg-orange-50 text-orange-700 border border-orange-200" : "bg-green-50 text-green-700 border border-green-200"}>
          {i > 0 ? `${fmtMin(horas)} perdidas` : "Sem impedimentos"}
        </Badge>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-2 border-t border-slate-100 space-y-3">
          <ActivityList title="🌅 Atividades — Manhã" list={report.atividades_manha} />
          <ActivityList title="🌇 Atividades — Tarde" list={report.atividades_tarde} />
          {(report.impedimentos || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">⚠️ Impedimentos</h4>
              <ul className="space-y-2">
                {report.impedimentos.map((imp, idx) => (
                  <li key={idx} className="bg-orange-50 border border-orange-100 rounded p-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-orange-900">{imp.tipo}</span>
                      <span className="text-xs font-mono text-orange-700">
                        início: {imp.horario_inicio || "—"} · duração: <strong>{fmtMin(imp.duracao_minutos)}</strong>
                      </span>
                    </div>
                    {imp.comentario && <div className="text-xs text-orange-900 mt-2 whitespace-pre-wrap">{imp.comentario}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ActivityList = ({ title, list }) => {
  if (!list || !list.length) return <div className="text-xs text-slate-400">{title}: nenhuma</div>;
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</h4>
      <ul className="space-y-2">
        {list.map((a, idx) => (
          <li key={idx} className="border-l-2 border-slate-200 pl-3 py-1">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-slate-700">{a.atividade}</span>
              <span className="text-xs text-slate-500 font-mono">{a.inicio || "—"} → {a.fim || "—"}</span>
            </div>
            {a.comentario && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{a.comentario}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Heatmap = ({ dayMins }) => {
  const dates = Object.keys(dayMins).sort();
  if (dates.length === 0) {
    return <div className="text-center text-sm text-slate-400 py-6">Sem dados no período</div>;
  }
  const minDate = new Date(dates[0] + "T00:00:00");
  const maxDate = new Date(dates[dates.length - 1] + "T00:00:00");
  const firstMonday = new Date(minDate);
  const dow = (firstMonday.getDay() + 6) % 7;
  firstMonday.setDate(firstMonday.getDate() - dow);

  const weeks = [];
  const cursor = new Date(firstMonday);
  while (cursor <= maxDate) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(cursor);
      d.setDate(d.getDate() + i);
      week.push(d);
    }
    weeks.push(week);
    cursor.setDate(cursor.getDate() + 7);
  }

  const maxMins = Math.max(...Object.values(dayMins), 1);
  const colorFor = (mins) => {
    if (!mins) return "#f1f5f9";
    const t = Math.min(mins / maxMins, 1);
    const r = Math.round(220 + (232 - 220) * t);
    const g = Math.round(252 + (118 - 252) * t);
    const b = Math.round(231 + (45 - 231) * t);
    return `rgb(${r}, ${g}, ${b})`;
  };
  const dowLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
  const toISO = (d) => d.toISOString().slice(0, 10);
  const fmtDate = (d) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;

  return (
    <div className="overflow-x-auto">
      <table className="text-xs" style={{ borderSpacing: 3, borderCollapse: "separate" }}>
        <thead>
          <tr>
            <th></th>
            {weeks.map((w, i) => (
              <th key={i} className="text-slate-400 font-mono text-[10px] px-1">{fmtDate(w[0])}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dowLabels.map((lbl, dowIdx) => (
            <tr key={lbl}>
              <td className="text-slate-500 font-medium pr-2">{lbl}</td>
              {weeks.map((w, wi) => {
                const d = w[dowIdx];
                const iso = toISO(d);
                const mins = dayMins[iso];
                const inRange = d >= minDate && d <= maxDate;
                const c = inRange ? colorFor(mins || 0) : "transparent";
                const title = inRange
                  ? (mins !== undefined ? `${iso}: ${fmtMin(mins)}` : `${iso}: sem dados`)
                  : "";
                return (
                  <td
                    key={wi}
                    title={title}
                    style={{
                      background: c,
                      width: 32,
                      height: 24,
                      borderRadius: 3,
                      border: inRange ? "none" : "1px dashed #e5e7eb",
                    }}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 mt-3 text-xs text-slate-500 flex-wrap">
        <span>Menos</span>
        <span style={{ background: "#f1f5f9", width: 18, height: 14, borderRadius: 2 }} />
        <span style={{ background: "#fde6cf", width: 18, height: 14, borderRadius: 2 }} />
        <span style={{ background: "#f3a76b", width: 18, height: 14, borderRadius: 2 }} />
        <span style={{ background: "#E8762D", width: 18, height: 14, borderRadius: 2 }} />
        <span>Mais</span>
        <span className="ml-4">Pico no período: <strong>{fmtMin(maxMins)}</strong></span>
      </div>
    </div>
  );
};

// ============== Helpers ==============

const computeStats = (data) => {
  if (!data || !data.relatorios) {
    return { totalReports: 0, totalImps: 0, totalMin: 0, avgMin: 0, allImps: [], byTypeEntries: [], dailyHours: [], dayMinsMap: {} };
  }
  const reports = data.relatorios;
  const allImps = [];
  reports.forEach(r => (r.impedimentos || []).forEach(i => allImps.push({ ...i, data_relatorio: r.data_relatorio })));
  const totalMin = allImps.reduce((s, i) => s + (i.duracao_minutos || 0), 0);
  const daysWithImp = new Set();
  reports.forEach(r => { if ((r.impedimentos || []).length > 0) daysWithImp.add(r.data_relatorio); });
  const avgMin = daysWithImp.size > 0 ? Math.round(totalMin / daysWithImp.size) : 0;

  const byType = {};
  allImps.forEach(i => {
    const k = i.tipo || "—";
    if (!byType[k]) byType[k] = { count: 0, mins: 0 };
    byType[k].count += 1;
    byType[k].mins += (i.duracao_minutos || 0);
  });
  const byTypeEntries = Object.entries(byType).sort((a, b) => b[1].mins - a[1].mins);

  const dailyMap = {};
  reports.forEach(r => {
    dailyMap[r.data_relatorio] = (r.impedimentos || []).reduce((s, i) => s + (i.duracao_minutos || 0), 0);
  });
  const dailyHours = Object.entries(dailyMap).sort().map(([date, mins]) => ({ date, hours: +(mins / 60).toFixed(2) }));

  return {
    totalReports: reports.length,
    totalImps: allImps.length,
    totalMin,
    avgMin,
    allImps,
    byTypeEntries,
    dailyHours,
    dayMinsMap: dailyMap,
  };
};

const filterImps = (imps, q) => {
  if (!q) return imps;
  const ql = q.toLowerCase();
  return imps.filter(i => `${i.tipo} ${i.comentario || ""}`.toLowerCase().includes(ql));
};

export default StudyDashboard;
